import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Save, ScanLine } from 'lucide-react';
import { Dropzone } from './Dropzone';
import HistoryDialog from './HistoryDialog';
import UpdatedAtBadge from './UpdatedAtBadge';
import DraftBanner from './DraftBanner';
import { useAutoSaveDraft } from '../../hooks/useAutoSaveDraft';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const defaultData = {
  id: 'donation_qr',
  headline: 'Support Our Mission',
  title: 'Scan to Donate',
  subtitle: 'Every ringgit changes a life',
  description: '',
  qr_image: '',
  qr_label: 'DuitNow QR',
  bank_name: '',
  account_name: 'Educational, Welfare and Research Foundation',
  account_number: '',
  reference_note: '',
  whatsapp_number: '',
  whatsapp_link: '',
  tax_note: 'Tax-deductible under Section 44(6) 🇲🇾',
  published: true,
};

const resolveImg = (src) => {
  if (!src) return '';
  if (src.startsWith('http')) return src;
  return `${BACKEND_URL}${src}`;
};

const DonationQRManager = () => {
  const [data, setData] = useState(defaultData);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    try {
      const res = await adminApi.get('/donation-qr');
      if (res.data && res.data.id) setData({ ...defaultData, ...res.data });
    } catch (e) {
      console.error('load donation-qr', e);
    }
    setLoaded(true);
  };

  useEffect(() => { load(); }, []);

  const { hasDraft, draftAt, readDraft, discardDraft, markSaved } = useAutoSaveDraft({
    storageKey: 'draft:donation_qr',
    data,
    enabled: loaded,
    savedAt: data.updated_at,
  });

  const restoreDraft = () => {
    const draft = readDraft();
    if (draft) setData({ ...defaultData, ...draft });
    discardDraft();
  };

  const save = async () => {
    setLoading(true);
    try {
      await adminApi.post('/donation-qr', data);
      const res = await adminApi.get('/donation-qr');
      if (res.data) setData({ ...defaultData, ...res.data });
      markSaved(res.data || data);
      toast({ title: 'Saved', description: 'Donation QR & bank details updated' });
    } catch (e) {
      toast({ title: 'Error', description: 'Save failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  return (
    <div className="space-y-6" data-testid="donation-qr-manager">
      {hasDraft && (
        <DraftBanner
          draftAt={draftAt}
          onRestore={restoreDraft}
          onDiscard={discardDraft}
          testid="donation-qr-draft-banner"
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ScanLine className="w-6 h-6 text-[#1565C0]" />
          <h2 className="text-2xl font-bold">Donation QR &amp; Bank Details</h2>
          <UpdatedAtBadge updatedAt={data.updated_at} />
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="qr-published" className="text-sm">Published</Label>
            <Switch
              id="qr-published"
              checked={data.published}
              onCheckedChange={(v) => set('published', v)}
              data-testid="donation-qr-published-switch"
            />
          </div>
          <HistoryDialog collection="donation_qr" docId="donation_qr" onRestored={load} />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        {/* Editor */}
        <Card>
          <CardHeader><CardTitle>Edit Content</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Eyebrow / Headline</Label>
                <Input value={data.headline} onChange={(e) => set('headline', e.target.value)} data-testid="qr-headline-input" />
              </div>
              <div>
                <Label>Title (last word is highlighted)</Label>
                <Input value={data.title} onChange={(e) => set('title', e.target.value)} data-testid="qr-title-input" />
              </div>
            </div>

            <div>
              <Label>Subtitle</Label>
              <Input value={data.subtitle} onChange={(e) => set('subtitle', e.target.value)} data-testid="qr-subtitle-input" />
            </div>

            <div>
              <Label>Description (under QR)</Label>
              <Textarea rows={3} value={data.description} onChange={(e) => set('description', e.target.value)} data-testid="qr-description-input" />
            </div>

            <div className="pt-2 border-t">
              <Label className="font-semibold">QR Code Image</Label>
              {data.qr_image && (
                <img
                  src={resolveImg(data.qr_image)}
                  alt="QR preview"
                  className="w-48 h-48 object-contain rounded border mt-2 mb-2 bg-white"
                  data-testid="qr-image-preview"
                />
              )}
              <Dropzone compact onUpload={(url) => set('qr_image', url)} label="Drop DuitNow QR image (PNG/JPG)" />
              <div className="mt-2">
                <Label>QR Label (badge above the QR)</Label>
                <Input value={data.qr_label} onChange={(e) => set('qr_label', e.target.value)} placeholder="DuitNow QR" data-testid="qr-label-input" />
              </div>
            </div>

            <div className="pt-2 border-t grid md:grid-cols-2 gap-3">
              <div>
                <Label>Bank Name</Label>
                <Input value={data.bank_name} onChange={(e) => set('bank_name', e.target.value)} placeholder="e.g. Maybank" data-testid="qr-bank-name-input" />
              </div>
              <div>
                <Label>Account Number</Label>
                <Input value={data.account_number} onChange={(e) => set('account_number', e.target.value)} placeholder="e.g. 5644 7212 3456" data-testid="qr-account-number-input" />
              </div>
              <div className="md:col-span-2">
                <Label>Account Name</Label>
                <Input value={data.account_name} onChange={(e) => set('account_name', e.target.value)} data-testid="qr-account-name-input" />
              </div>
            </div>

            <div className="pt-2 border-t">
              <Label>Reference / Instruction Note (yellow callout)</Label>
              <Textarea rows={3} value={data.reference_note} onChange={(e) => set('reference_note', e.target.value)} data-testid="qr-reference-input" />
            </div>

            <div className="pt-2 border-t grid md:grid-cols-2 gap-3">
              <div>
                <Label>WhatsApp Number (display)</Label>
                <Input value={data.whatsapp_number} onChange={(e) => set('whatsapp_number', e.target.value)} placeholder="+60 3-7960 1479" data-testid="qr-whatsapp-number-input" />
              </div>
              <div>
                <Label>WhatsApp Link (click-to-chat URL)</Label>
                <Input value={data.whatsapp_link} onChange={(e) => set('whatsapp_link', e.target.value)} placeholder="https://wa.me/60379601479" data-testid="qr-whatsapp-link-input" />
              </div>
            </div>

            <div>
              <Label>Tax Note (footer)</Label>
              <Input value={data.tax_note} onChange={(e) => set('tax_note', e.target.value)} data-testid="qr-tax-note-input" />
            </div>

            <Button onClick={save} disabled={loading} className="bg-[#1565C0]" data-testid="save-donation-qr-btn">
              <Save className="w-4 h-4 mr-2" />{loading ? 'Saving…' : 'Save Donation Settings'}
            </Button>
          </CardContent>
        </Card>

        {/* Live preview */}
        <Card className="border-[#D4AF37]/30 sticky top-4">
          <CardHeader><CardTitle className="text-base">Live Preview</CardTitle></CardHeader>
          <CardContent>
            <div className="rounded-2xl border-4 border-[#0A1628] bg-white p-3 max-w-xs mx-auto shadow">
              {data.qr_image ? (
                <img
                  src={resolveImg(data.qr_image)}
                  alt="QR"
                  className="w-full h-auto rounded"
                />
              ) : (
                <div className="aspect-square flex items-center justify-center bg-gray-100 rounded text-gray-400 text-sm">
                  No QR uploaded
                </div>
              )}
            </div>
            <p className="text-center text-xs text-gray-600 mt-2">{data.qr_label}</p>

            <div className="mt-4 text-sm space-y-1">
              {data.bank_name && <div><strong>Bank:</strong> {data.bank_name}</div>}
              {data.account_name && <div><strong>Name:</strong> {data.account_name}</div>}
              {data.account_number && <div><strong>Acc No:</strong> {data.account_number}</div>}
            </div>
            {data.reference_note && (
              <div className="mt-3 p-2 rounded bg-amber-50 border-l-4 border-amber-400 text-xs text-amber-900">
                {data.reference_note}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DonationQRManager;
